import * as React from 'react';

/**
 * Lets a document host (the document page) offer a maximise control in the editor toolbar.
 * Hosts own the state; editors nested inside panels, previews or reviews receive `null` and
 * show no control. Ported from Solution Studio's `document-maximise-context.ts`.
 */
export interface DocumentMaximiseControl {
  maximised: boolean;
  toggle: () => void;
}

export const DocumentMaximiseContext = React.createContext<DocumentMaximiseControl | null>(null);

export function useDocumentMaximise(): DocumentMaximiseControl | null {
  return React.useContext(DocumentMaximiseContext);
}

/** Class that marks the maximised workspace; floating panel toggles portal into it. */
export const DOCUMENT_MAXIMISED_CLASS = 'document-workspace-maximised';

/** Escape belongs to an open dialog, menu or list first; only a bare Escape restores the editor. */
const ESCAPE_OWNERS = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-escape-owner]';

export function escapeRestoresMaximised(event: Pick<KeyboardEvent, 'key' | 'defaultPrevented' | 'target'>): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented) return false;
  return !(event.target instanceof Element && event.target.closest(ESCAPE_OWNERS));
}

/**
 * State for a host that can maximise its document workspace: Esc restores, page scrolling is
 * locked while maximised, and focus returns to the control that maximised it.
 */
export function useDocumentMaximiseHost(): DocumentMaximiseControl {
  const [maximised, setMaximised] = React.useState(false);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const toggle = React.useCallback(() => setMaximised(current => {
    if (!current) returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    return !current;
  }), []);
  React.useEffect(() => {
    if (!maximised) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!escapeRestoresMaximised(event)) return;
      event.preventDefault();
      setMaximised(false);
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      const target = returnFocus.current;
      returnFocus.current = null;
      if (target?.isConnected) window.requestAnimationFrame(() => target.focus());
    };
  }, [maximised]);
  return React.useMemo(() => ({ maximised, toggle }), [maximised, toggle]);
}
